<template>
  <v-container>
    <v-card-title>
      <h3>Stakeholders</h3>

      <v-spacer />
      <v-text-field
        v-model="searchTerm"
        append-icon="search"
        label="Search"
        single-line
        hide-details
      />
    </v-card-title>
    <v-row
      style="padding-left: 2%; padding-right: 1%;"
      justify="center"
    >
      <v-col cols="4">
        <FilterByDropdown
          ref="programFilter"
          filter-type="program"
        />
      </v-col>
      <v-col cols="4">
        <FilterByDropdown
          ref="projectFilter"
          filter-type="project"
        />
      </v-col>
      <v-col cols="4">
        <FilterByDropdown
          ref="surveyFilter"
          filter-type="survey"
        />
      </v-col>
    </v-row>
    <v-divider />
    <v-row
      style="padding-left: 2%; padding-right: 1%;"
      justify="center"
    >
      <v-col :cols="setBreakpoint()">
        <v-select
          v-model="filter.city"
          class="formatting"
          label="Cities"
          :items="cityList"
        />
      </v-col>
      <v-col :cols="setBreakpoint()">
        <v-select
          v-model="filter.state"
          class="formatting"
          label="States"
          :items="stateList"
        />
      </v-col>
      <v-col :cols="setBreakpoint()">
        <v-select
          v-model="filter.zip"
          class="formatting"
          label="Zip Codes"
          :items="zipList"
        />
      </v-col>
      <v-btn
        outlined
        small
        @click="reset"
      >
        Clear Filters
      </v-btn>
    </v-row>
  </v-container>
</template>
<script>
import FilterByDropdown from "@/components/Dashboard/Charts/FilterByDropdown";
import { mapActions, mapState } from "vuex";

export default {
  name: "StakeholderFilterOptions",
  components: {
    FilterByDropdown
  },
  data() {
    return {
      searchTerm: "",
      filter: {
        city: "",
        state: "",
        zip: ""
      }
    };
  },
  computed: {
    ...mapState({
      stakeholders: state => state.stakeholders.stakeholders,
      filteredStakeholders: state => state.stakeholderList.filteredStakeholders,
      responses: state => state.response.responses,
      selectedProgram: state => state.programs.selectedProgram || "",
      selectedProject: state => state.projects.selectedProject || "",
      selectedSurvey: state => state.survey.selectedSurvey || ""
    }),
    cityList() {
      const cities = this.stakeholders.map(stakeholder => stakeholder.city).filter(Boolean);
      this.stakeholders.forEach(stakeholder => {
        if (stakeholder.city !== null && stakeholder.city !== "") {
          cities.push(stakeholder.city);
        }
      });
      let citylist = [...new Set(cities)];
      citylist.sort();
      citylist.unshift("");
      return citylist;
    },
    stateList() {
      const states = this.stakeholders.map(stakeholder => stakeholder.state).filter(Boolean);
      this.stakeholders.forEach(stakeholder => {
        if (stakeholder.state !== null && stakeholder.state !== "") {
          states.push(stakeholder.state);
        }
      });
      let statelist = [...new Set(states)];
      statelist.sort();
      statelist.unshift("");
      return statelist;
    },
    zipList() {
      const zips = this.stakeholders.map(stakeholder => stakeholder.postal_code);
      this.stakeholders.forEach(stakeholder => {
        zips.push(stakeholder.postal_code);
      });
      let ziplist = [...new Set(zips)];
      ziplist.sort();
      ziplist.unshift("");
      return ziplist;
    },
    allFilters() {
      return [
        this.selectedProgram,
        this.selectedProject,
        this.selectedSurvey,
        this.filter.city,
        this.filter.state,
        this.filter.zip,
        this.searchTerm
      ].join();
    },
    multiFilterFields() {
      return [this.selectedProgram, this.selectedProject, this.selectedSurvey];
    },
    toggleClearFilter() {
      if (this.filter.city || this.filter.zip || this.filter.state) {
        return false;
      } else {
        return true;
      }
    }
  },
  watch: {
    allFilters: {
      handler() {
        let result;
        // if multi filter fields are null, use stakeholder list as source rather than responses
        if (!this.multiFilterFields.every(Boolean)) {
          result = this.stakeholders;
        } else {
          result = this.multiFilter(
            this.selectedProgram.name,
            this.selectedProject.name,
            this.selectedSurvey.name,
            this.responses
          );
        }
        result = this.filterBySearchTerm(
          this.filterByDropdowns(result, this.filter),
          this.searchTerm
        );
        this.updateFilteredStakeholders(result);
      },
      deep: true
    }
  },
  methods: {
    ...mapActions({
      updateSelectedProgram: "programs/updateSelectedProgram",
      updateSelectedProject: "projects/updateSelectedProject",
      filterProjects: "projects/filterProjects",
      updateSelectedSurvey: "survey/updateSelectedSurvey",
      updateFilteredStakeholders: "stakeholderList/updateFilteredStakeholders"
    }),
    filterByDropdowns(stakeholders, filters) {
      const filtered = stakeholders.filter(item => {
        let city = filters.city === "" ? true : item.city === filters.city;
        let state = filters.state === "" ? true : item.state === filters.state;
        let zip =
          filters.zip === ""
            ? true
            : item.postal_code.toString() === filters.zip.toString();
        return city && state && zip;
      });
      return filtered;
    },
    filterBySearchTerm(array, search) {
      return array.filter(o =>
        Object.keys(o).some(k =>
          String(o[k])
            .toLowerCase()
            .includes(search.toLowerCase())
        )
      );
    },
    setBreakpoint() {
      if (this.$vuetify.breakpoint.xs) {
        return 12;
      } else {
        return 4;
      }
    },
    getStakeholder(stakeholders, response) {
      return stakeholders.find(
        stakeholder => stakeholder.pk === response.survey_taker_id
      );
    },
    multiFilter(program, project, survey, responses) {
      const byProgram = r =>
        r.survey_metadata.meta_data.dashboardFiltering.program === program;
      const byProject = r =>
        r.survey_metadata.meta_data.dashboardFiltering.project === project;
      const bySurvey = r =>
        r.survey_metadata.meta_data.dashboardFiltering.survey === survey;

      const filters = [];
      if (program) {
        filters.push(byProgram);
      }
      if (project) {
        filters.push(byProject);
      }
      if (survey) {
        filters.push(bySurvey);
      }

      const results = filters.reduce(
        (data, filter) => data.filter(filter),
        responses
      );
      const stakeholders = results.map(r =>
        this.getStakeholder(this.stakeholders, r)
      );
      const distinctStakeholders = [...new Set(stakeholders)];
      return distinctStakeholders;
    },
    reset() {
      this.filter.city = '';
      this.filter.state = '';
      this.filter.zip = '';
      this.searchTerm = '';
      this.$refs.programFilter.clearFilters();
      this.$refs.projectFilter.clearFilters();
      this.$refs.surveyFilter.clearFilters();
    }
  }
};
</script>
